// Authorize.Net config for the shared ReconWorkspace (the canonical, feature-rich PSP — full ACH:
// recurring Subscriptions, one-time, returns/voids, subscription mapping). Reads the SAME existing
// /api/manage/authnet-reconcile* endpoints the old page uses; nothing backend changes. Lean canonical
// columns per docs/recon-workspace-design.md: Date · Amount · Donor · Method · Subscription · Order? ·
// Match · Actions, <=2 lines each, heavy detail pushed to the detail dialog.
import React from 'react';
import { Box, Typography, Chip, Link, Tooltip, Button } from '@mui/material';
import { AUTHNET_NAV_ITEMS } from './authnetNav';
import { get, post } from '../../utils/api';
import { customers } from '../../services/kioskApi';
import { fmtMoney, fmtDate } from '../../components/recon/reconShared';
import AuthnetTxnDetailDialog from '../../components/recon/authnet/AuthnetTxnDetailDialog';
import DonorLink from '../../components/recon/DonorLink';
import CreateOrderDialog from '../../components/authnet/CreateOrderDialog';
import SubscriptionMappingDialog from '../../components/recon/SubscriptionMappingDialog';

// Authorize.Net's EXISTING mapping endpoints, handed to the shared SubscriptionMappingDialog (same UI for
// every PSP). Exported so the Subscriptions page uses the exact same definition. No data-source change.
export const authnetMapApi = {
  suggest: (sub) => get('/api/manage/authnet-subscriptions/suggest', { account: sub.account, id: sub.id }),
  devoteeSearch: (q) => get('/api/manage/authnet/devotee-search', { q }),
  productSearch: (q) => get('/api/manage/authnet/product-search', { q }),
  customerPledges: (customerId) => get('/api/manage/authnet/customer-pledges', { customer_id: customerId }),
  saveMapping: ({ sub, customerId, splits }) => post('/api/manage/authnet-subscriptions/map', {
    account: sub.account, subscription_id: sub.id, customer_id: customerId, sub_name: sub.name, splits,
  }),
};

const isEcheck = (m) => String(m || '').toLowerCase().includes('echeck');
const methodLabel = (m) => (isEcheck(m) ? 'eCheck (ACH)' : (m || 'card'));
const methodShort = (m) => (isEcheck(m) ? 'ACH' : (m || 'card'));

const MATCH_LABEL = {
  trans_id: 'AuthNet ID on order', manual_link: 'AuthNet ID (linked)', invoice: 'Invoice # = order #',
  name_amount: 'Donor name + amount', amount_date: 'Amount + date',
};
const matchLabelOf = (r) => MATCH_LABEL[r.match_method] || '—';
const isFuzzyMatch = (m) => m === 'name_amount' || m === 'amount_date';

const bucketOf = (s) => (
  (s === 'matched' || s === 'needs_link') ? 'order'
    : (s === 'unbooked') ? 'no_order'
      : (s === 'returned') ? 'returned'
        : s
);

const link = ({ row, orderId }) => post('/api/manage/authnet-reconcile/link', { trans_id: row.trans_id, account: row.account, order_id: orderId });

// Create an order for an unbooked row: mapped Subscription → preview+confirm from the mapping; else
// fall back to the manual Post Payment form, pre-filled. (Same behavior as the existing page.)
async function createOrder(r, ctx) {
  if (ctx.mapFor(r)?.splits?.length) { ctx.openCreate(r); return; }
  let user = null;
  if (r.donor_name) {
    try { const list = await customers.searchUsers(r.donor_name); if (Array.isArray(list) && list.length === 1) user = list[0]; } catch { /* staff searches manually */ }
  }
  ctx.navigate('/post-payment', {
    state: {
      user: user || undefined,
      reconPrefill: { authnetTransId: r.trans_id, authnetAccount: r.account, amount: Number(r.amount_num), name: r.donor_name || '' },
    },
  });
}

const peekCtxOf = (r) => ({ orderId: r.matched_order_id, orderNumber: r.matched_order_number, transId: r.trans_id, account: r.account, row: r, canLink: r.status === 'needs_link' });

const authnetConfig = {
  key: 'authnet',
  overline: 'Payments · Authorize.Net',
  provider: 'authnet',
  title: 'Tx Reconciliation',
  // Workspace section nav (Dashboard / Subscriptions / Tx Reconciliation), rendered by ReconWorkspace
  // under the account row. Dashboard + Subscriptions reuse the existing screens; Tx stays on this new page.
  sectionNav: { active: 'reconciliation', items: AUTHNET_NAV_ITEMS },
  filters: { period: true, account: true, search: true },
  accountAllTab: true,          // AuthNet supports a cross-account "All accounts" view
  defaultAccount: 'all',
  perPage: 25,
  runTooltip: 'Reconcile now — pulls recent Authorize.Net settlements (both accounts) and re-checks open items against orders',

  list: ({ year, month, account }) => get('/api/manage/authnet-reconcile', { year, month, account }),
  run: () => post('/api/manage/authnet-reconcile/run', {}),
  rowKey: (r) => `${r.account}:${r.trans_id}`,
  bucketOf,
  searchText: (r, ctx) => `${r.donor_name || ''} ${r.trans_id} ${r.subscription_id || ''} ${r.matched_order_number || ''} ${r.amount_num} ${ctx.mapFor(r)?.customer_name || ''} ${ctx.allocSummary(ctx.mapFor(r))}`,

  loadMaps: async () => {
    const r = await get('/api/manage/authnet-subscriptions/maps');
    const o = {}; (r?.mappings || []).forEach((x) => { o[`${x.account}:${x.subscription_id}`] = x; });
    return o;
  },
  mapKey: (r) => (r.subscription_id ? `${r.account}:${r.subscription_id}` : '__none__'),

  buckets: [
    { key: 'no_order', label: 'No order' },
    { key: 'order', label: 'Order' },
    { key: 'returned', label: 'Returned' },
    { key: 'all', label: 'All' },
  ],
  tabNote: (tab) => (
    tab === 'returned'
      ? 'These eCheck (ACH) payments settled and then came back (NSF / closed account / unauthorized). The money was reversed even though the order may still read as paid — review each and follow up.'
      : tab === 'order'
        ? 'These have an order. The Match column flags only rows matched by donor name + amount that don’t yet carry the Authorize.Net id — verify (preview) then Link.'
        : null
  ),

  statTiles: (data) => {
    const c = data?.counts || {}; const a = data?.amounts || {};
    return [
      { label: 'Authorize.Net payments', value: data?.total ?? '—', sub: 'in this period · click for all', color: null, tabKey: 'all' },
      { label: 'Order', value: (c.matched ?? 0) + (c.needs_link ?? 0), sub: 'has an order', color: 'success', tabKey: 'order' },
      { label: 'No order · at risk', value: c.unbooked ?? 0, sub: fmtMoney(a.unbooked), color: 'error', tabKey: 'no_order' },
      { label: 'Returned (ACH came back)', value: c.returned ?? 0, sub: fmtMoney(a.returned), color: 'warning', tabKey: 'returned' },
    ];
  },

  link,
  orderPeek: {
    link: (peekCtx) => link({ row: peekCtx.row, orderId: peekCtx.orderId }),
    idMetaKeys: ['authnet_trans_id'],
    idLabel: 'Authorize.Net id on order',
  },

  columns: [
    {
      key: 'date', header: 'Date', width: '11%',
      render: (r) => fmtDate(r.created_at),
    },
    {
      key: 'amount', header: 'Amount', width: '10%', align: 'right',
      render: (r) => (
        <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: r.status === 'unbooked' ? 'error.main' : (r.status === 'returned' ? 'warning.dark' : 'inherit') }}>{fmtMoney(r.amount_num)}</Box>
      ),
    },
    {
      key: 'donor', header: 'Donor', width: '22%',
      render: (r, ctx) => (
        <>
          {r.donor_name
            ? <DonorLink name={r.donor_name} customerId={ctx.mapFor(r)?.customer_id} />
            : <Typography variant="caption" sx={{ color: 'text.secondary' }}>no donor captured</Typography>}
          <Link component="button" type="button" underline="hover" onClick={() => ctx.openDetail(r)} sx={{ display: 'block', fontFamily: 'monospace', fontSize: 11.5, textAlign: 'left', mt: 0.25, wordBreak: 'break-all' }}>{r.trans_id}</Link>
        </>
      ),
    },
    {
      key: 'method', header: 'Method', width: '8%',
      render: (r) => (
        <Tooltip title={`${methodLabel(r.method)}${r.account_mask ? ` · ${r.account_mask}` : ''}`}>
          <Chip size="small" label={methodShort(r.method)} color={isEcheck(r.method) ? 'info' : 'default'} variant={isEcheck(r.method) ? 'filled' : 'outlined'} />
        </Tooltip>
      ),
    },
    {
      key: 'subscription', header: 'Subscription', width: '15%',
      render: (r, ctx) => {
        if (!r.subscription_id) return <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>;
        const m = ctx.mapFor(r);
        return (
          <>
            <Chip size="small" variant="outlined" color="secondary" clickable onClick={() => ctx.openDetail(r)}
              label={`Sub ${r.subscription_id}${r.subscription_pay_num != null ? ` · pay ${r.subscription_pay_num}` : ''}`} />
            {m && (
              <Tooltip title={`Will post to ${m.customer_name || 'devotee'} — ${ctx.allocSummary(m) || 'mapped allocation'}`}>
                <Typography variant="caption" sx={{ display: 'block', color: 'info.main', mt: 0.25 }}>↳ {m.customer_name || 'mapped'}</Typography>
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      key: 'order', header: 'Order?', width: '15%',
      render: (r, ctx) => {
        if (bucketOf(r.status) === 'order') {
          return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Chip size="small" color={r.status === 'matched' ? 'success' : 'warning'} variant={r.status === 'matched' ? 'outlined' : 'filled'}
                label={`Order ${r.matched_order_number || ''}`.trim()}
                onClick={r.matched_order_id ? () => ctx.openOrderPeek(peekCtxOf(r)) : undefined}
                sx={r.matched_order_id ? { cursor: 'pointer' } : undefined} />
            </Box>
          );
        }
        if (r.status === 'returned') return (
          <Box>
            <Chip size="small" color="warning" label="Returned" />
            {r.returned_reason && <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark', mt: 0.25 }}>{r.returned_reason}</Typography>}
          </Box>
        );
        if (r.status === 'refunded') return <Chip size="small" variant="outlined" label="Refunded" />;
        if (r.status === 'voided') return <Chip size="small" variant="outlined" label="Voided" />;
        return <Chip size="small" color="error" label="No order" />;
      },
    },
    {
      key: 'match', header: 'Match', width: '9%',
      render: (r) => (isFuzzyMatch(r.match_method)
        ? <Typography variant="caption" sx={{ color: 'warning.dark' }}>{matchLabelOf(r)}</Typography>
        : null),
    },
    {
      key: 'actions', header: 'Action', width: '10%', align: 'right',
      render: (r, ctx) => {
        if (r.status === 'unbooked') {
          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', alignItems: 'flex-end' }}>
              {r.subscription_id && !ctx.mapFor(r) && (
                <Button size="small" variant="outlined" color="secondary" sx={{ whiteSpace: 'nowrap' }}
                  onClick={() => ctx.openMap({ id: r.subscription_id, account: r.account, amount: r.amount_num, donor_name: r.donor_name, name: `Subscription ${r.subscription_id}` })}>
                  Map subscription
                </Button>
              )}
              <Button size="small" variant="contained" sx={{ whiteSpace: 'nowrap' }} onClick={() => createOrder(r, ctx)}>Create order</Button>
            </Box>
          );
        }
        if (r.status === 'needs_link') return <Button size="small" variant="contained" onClick={() => ctx.linkRow(r)}>Link</Button>;
        return null;
      },
    },
  ],

  renderDialogs: (ctx) => (
    <>
      <AuthnetTxnDetailDialog ctx={ctx.slots.detailRow} onClose={ctx.closeDetail} />
      <CreateOrderDialog
        open={!!ctx.slots.createRow}
        row={ctx.slots.createRow}
        onClose={ctx.closeCreate}
        onCreated={(res) => { ctx.closeCreate(); ctx.setToast(res?.order_number ? `Order ${res.order_number} created` : 'Order created'); ctx.reload(); }}
      />
      <SubscriptionMappingDialog
        open={!!ctx.slots.mapSub} sub={ctx.slots.mapSub} provider="authnet" account={ctx.slots.mapSub?.account} api={authnetMapApi}
        onClose={ctx.closeMap} onSaved={() => { ctx.closeMap(); ctx.setToast('Mapping saved'); ctx.reloadMaps(); }} />
    </>
  ),
};

export default authnetConfig;
